import { useTOCContext } from '@context/TOCContext';
import ConfirmDialog from '@deckManagement/components/Common/Dialogs/ConfirmDialog';
import PageLockIndicator from '@deckManagement/components/TOC/CellRenderers/PageLockIndicator';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { ScheduleType } from '@services/api/deckManagement/graphql/schema/API';
import { MouseEvent, useState } from 'react';
import { MenuItemConfig } from 'types/Common.types';
import { TOCActionsCellRendererProps } from 'types/TOC.types';

const ActionsCellRenderer = ({
  data,
  onToggleVisibility,
  onStartRename,
  onStartSimultaneousEdit,
  onDelete,
  onPageCopy,
  onMoveToActive,
  currentView,
  isEditingDisabled,
  pageLockMap,
}: TOCActionsCellRendererProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const { togglePageActive, copyPage } = useTOCContext();

  /**
   * Handles click event on the actions menu and button.
   * @param {React.MouseEvent<HTMLElement>} event
   */
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handles closing the actions menu.
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Handles delete page option click.
   * Opens the delete confirmation dialog.
   */
  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
    handleClose();
  };

  /**
   * Handles delete confirmation.
   * Closes the delete confirmation dialog and calls the onDelete callback.
   */
  const handleDeleteConfirm = () => {
    setIsDeleteDialogOpen(false);
    if (data?.toc_id && onDelete) {
      onDelete(data.toc_id, data?.page_name);
    }
  };

  /**
   * Handles visibility toggle.
   * Calls the onToggleVisibility callback with the toc_id.
   * @param {React.MouseEvent} event
   */
  const handleVisibilityToggle = (event: MouseEvent) => {
    event.stopPropagation();
    if (data?.toc_id && onToggleVisibility) {
      onToggleVisibility(data.toc_id);
    }
  };

  /**
   * Handles active toggle.
   * Calls the togglePageActive callback with the toc_id and highlights the row.
   */
  const handleActiveToggle = () => {
    if (data?.toc_id) {
      togglePageActive(data.toc_id);
    }
    handleClose();
  };

  /**
   * Handles copy page.
   * Calls the copyPage callback and starts simultaneous editing of both fields.
   */
  const handleCopy = () => {
    copyPage(data.toc_page_name, data.page_name, (newPageId: string) => {
      // Start simultaneous editing of both toc_page_name and page_name fields
      if (onStartSimultaneousEdit) {
        onStartSimultaneousEdit(newPageId);
      }
      // Notify the parent component about the copy operation
      if (onPageCopy) {
        onPageCopy(newPageId);
      }
    });
    handleClose();
  };

  /**
   * Gets the actions corresponding to active view
   */
  const activeViewActions =
    currentView === 'active'
      ? [
          {
            id: 'move-to-inactive',
            label: 'Move to Inactive Pages',
            icon: <CancelIcon fontSize='small' />,
            action: handleActiveToggle,
          },
        ]
      : [];

  /**
   * Gets the actions corresponding to inactive view
   */
  const inactiveViewActions =
    currentView === 'inactive'
      ? [
          {
            id: 'move-to-approved',
            label: 'Move to Active Pages - Approved',
            icon: <CheckCircleIcon fontSize='small' />,
            action: () => {
              handleClose();
              onMoveToActive?.(data.toc_id, ScheduleType.APPROVED);
            },
          },
          {
            id: 'move-to-modified',
            label: 'Move to Active Pages - Modified',
            icon: <CheckCircleIcon fontSize='small' />,
            action: () => {
              handleClose();
              onMoveToActive?.(data.toc_id, ScheduleType.MODIFIED);
            },
          },
          {
            id: 'delete',
            label: 'Delete',
            icon: <DeleteIcon fontSize='small' />,
            className: 'delete-icon',
            action: handleDeleteClick,
          },
        ]
      : [];

  /**
   * Allowed Action Menu items for Users
   * - Add items here, with handlers, as menu items expand
   */
  const menuItems: MenuItemConfig[] = [
    {
      id: 'copy',
      label: 'Copy',
      icon: <FileCopyIcon fontSize='small' />,
      action: handleCopy,
    },
    {
      id: 'rename',
      label: 'Rename',
      icon: <DriveFileRenameOutlineIcon fontSize='small' />,
      action: () => {
        handleClose();
        if (onStartRename && data?.toc_id) {
          onStartRename(data.toc_id);
        }
      },
    },
    ...activeViewActions,
    ...inactiveViewActions,
  ];

  return (
    <Box className='actions-cell' component='section'>
      <PageLockIndicator lock={data.page_name ? pageLockMap?.get(data.page_name) : undefined} />
      <IconButton
        className={`visibility-icon-button ${!data.is_visible ? 'hidden' : ''}`}
        onClick={handleVisibilityToggle}
        disabled={isEditingDisabled}
        aria-label={data.is_visible ? 'Hide page' : 'Show page'}
        size='small'
      >
        {data.is_visible ? <VisibilityIcon fontSize='inherit' /> : <VisibilityOffIcon fontSize='inherit' />}
      </IconButton>
      <IconButton
        className='menu-button'
        aria-label='more actions'
        aria-controls={open ? 'actions-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup='true'
        onClick={handleClick}
        size='small'
      >
        <MoreHorizIcon fontSize='inherit' />
      </IconButton>
      <Menu
        id='actions-menu'
        className='menu-dropdown'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        {menuItems.map((item) => (
          <MenuItem key={item.id} className='menu-item' onClick={item.action} disabled={isEditingDisabled}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>
              <Typography noWrap>{item.label}</Typography>
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
      {/* Dialog modals based on User Actions */}
      <ConfirmDialog
        isDialogOpen={isDeleteDialogOpen}
        setIsDialogOpen={setIsDeleteDialogOpen}
        handleConfirm={handleDeleteConfirm}
        dialogText='Are you sure you want to delete this page? Once deleted, it cannot be recovered.'
        cancelText='Cancel'
        confirmText='Yes , Delete'
        dialogTitle='Delete Page?'
      />
    </Box>
  );
};

export default ActionsCellRenderer;
